import { useEffect, useState } from 'react'
import { Dialog } from './Dialog'
import { TagReason } from './TagReason'
import { SearchPick } from './SearchPick'
import { useHubLang } from '../lib/i18n'
import { PHOTO_WHAT } from '../lib/hubCopy'
import {
  FACETS,
  answerPhotoFeature,
  loadTagOptions,
  photoFeaturesFor,
  type CrowdFeature,
  type PhotoFeatures,
  type VocabValue,
} from '../lib/photoRoom'

/**
 * 302 (FED-70) / 305 (FED-72). One's tags on a photo, in the feed's photo
 * viewer — for EVERYONE who can see the photo, since 305.
 *
 * David, 20 Oct: photos are read by One when they are posted, and what people
 * say about its tags teaches it. David, 22 Oct: "crowd sourcing this is
 * great" — so anyone who can see the photo says yes or no to each tag, adds
 * one One missed, and may say why. The poster's and the Gallery roles' word
 * decides; everybody else's answers count, and two of them settle a tag
 * nobody entitled to decide has answered (migration 305's resolution).
 * What a person sees is what the server resolved — the dialog re-reads
 * after every answer rather than guessing the outcome of a vote.
 *
 * One line under the photo, then a dialog: the viewer's space below the
 * picture is counted in lines (--lightbox-extra), and a facet-by-facet list
 * there would push the photo off a phone.
 */
export function PhotoWhatLine({ info, onOpen }: { info: PhotoFeatures; onOpen: () => void }) {
  const lang = useHubLang()
  const t = PHOTO_WHAT[lang]
  const labels = info.features.filter((f) => f.state !== 'rejected').map((f) => (lang === 'es' ? f.labelEs : f.labelEn))
  return (
    <p className="lightbox__what">
      <span className="lightbox__whattext">
        {labels.length
          ? info.decides
            ? t.oneSees(labels.join(', '))
            : t.oneSeesAsk(labels.join(', '))
          : info.described
            ? t.noTagsYet
            : t.stillLooking}
      </span>{' '}
      <button type="button" className="btn btn--link" onClick={onOpen}>
        {labels.length ? t.check : t.addTags}
      </button>
    </p>
  )
}

export function PhotoWhatDialog({
  path,
  info,
  onChanged,
  onClose,
}: {
  path: string
  info: PhotoFeatures
  onChanged: () => void
  onClose: () => void
}) {
  const lang = useHubLang()
  const t = PHOTO_WHAT[lang]
  const [options, setOptions] = useState<VocabValue[] | null>(null)
  const [state, setState] = useState<PhotoFeatures>(info)
  const [busy, setBusy] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  /* The reason box, for exactly one tag at a time. */
  const [asking, setAsking] = useState<{ kind: 'no' | 'add'; valueId: string; label: string; confidence: number | null } | null>(null)
  useEffect(() => setState(info), [info])

  useEffect(() => {
    let live = true
    loadTagOptions()
      .then((o) => live && setOptions(o))
      .catch(() => live && setNotice(t.couldNotLoad))
    return () => {
      live = false
    }
  }, [t.couldNotLoad])

  const label = (v: { labelEn: string; labelEs: string }) => (lang === 'es' ? v.labelEs : v.labelEn)

  async function answer(valueId: string, yes: boolean, reason: string | null) {
    setBusy(valueId)
    setNotice(null)
    try {
      await answerPhotoFeature(path, valueId, yes, reason)
      // What the server resolved, never a guess at the vote.
      const fresh = (await photoFeaturesFor([path])).get(path)
      if (fresh) setState(fresh)
      setAsking(null)
      onChanged()
    } catch {
      setNotice(t.couldNotSave)
    } finally {
      setBusy(null)
    }
  }

  const features = state.features
  const on = new Set(features.filter((f) => f.state !== 'rejected').map((f) => f.valueId))

  // A plain function, not a nested component: a component defined per render remounts its rows on every answer.
  const renderRow = (f: CrowdFeature) => {
    const name = label(f)
    const pct = f.suggestedBy ? null : Math.round((f.confidence ?? 0.5) * 100)
    const askNo = () => setAsking({ kind: 'no', valueId: f.valueId, label: name, confidence: f.suggestedBy ? null : (f.confidence ?? 0.5) })
    const cls =
      f.state === 'accepted' ? 'roomtag' : f.state === 'rejected' ? 'roomtag roomtag--rejected' : 'roomtag roomtag--suggested'
    return (
      <li key={f.valueId} className={cls} title={f.state === 'suggested' && pct !== null ? t.suggested(pct) : undefined}>
        {name}
        {f.state === 'suggested' && (
          <span className="roomtag__pct">{f.suggestedBy ? t.suggestedByPerson(f.suggestedBy) : `${pct}%`}</span>
        )}
        {(f.yes > 0 || f.no > 0) && <span className="roomtag__n">{t.counts(f.yes, f.no)}</span>}
        {f.myAnswer === true && <span className="roomtag__mine">{t.youAgreed}</span>}
        {f.myAnswer === false && <span className="roomtag__mine">{t.youSaidNo}</span>}
        {f.state !== 'accepted' && f.myAnswer !== true && (
          <button type="button" className="roomtag__yes" aria-label={`${name}: ${t.yes}`} title={t.yes} disabled={busy !== null}
            onClick={() => void answer(f.valueId, true, null)}>
            ✓
          </button>
        )}
        {f.state === 'accepted' && f.myAnswer !== true && (
          <button type="button" className="roomtag__yes" aria-label={`${name}: ${t.agree}`} title={t.agree} disabled={busy !== null}
            onClick={() => void answer(f.valueId, true, null)}>
            ✓
          </button>
        )}
        {f.state !== 'rejected' && f.myAnswer !== false && (
          <button type="button" className="roomtag__x" aria-label={`${name}: ${t.notInPhoto}`} title={t.notInPhoto} disabled={busy !== null}
            onClick={askNo}>
            ×
          </button>
        )}
      </li>
    )
  }

  return (
    <Dialog open title={t.dialogTitle} onClose={onClose} className="photowhat">
      <p className="photowhat__intro">{state.decides ? t.intro : t.introCrowd}</p>
      {notice && (
        <p className="photowhat__notice" role="status">
          {notice}
        </p>
      )}
      {FACETS.map((facet) => {
        const mine = features.filter((f) => f.facet === facet)
        const offer = (options ?? []).filter((v) => v.facet === facet && !on.has(v.id))
        return (
          <div key={facet} className="roomviewer__facet">
            <h3 className="roomviewer__fh">{t.facet[facet]}</h3>
            <ul className="roomtags">
              {mine.map(renderRow)}
              {offer.length > 0 && (
                <li className="roomtag__pick">
                  {/* 321: search the tag list rather than scroll a dropdown (David, 29 Oct). */}
                  <SearchPick
                    className="roomtag__add"
                    options={offer.map((v) => ({ id: v.id, label: label(v) }))}
                    label={`${t.facet[facet]}: ${t.add}`}
                    placeholder={t.add}
                    disabled={busy !== null}
                    onPick={(o) => setAsking({ kind: 'add', valueId: o.id, label: o.label, confidence: null })}
                  />
                </li>
              )}
              {mine.length === 0 && offer.length === 0 && <li className="photowhat__none">{t.none}</li>}
            </ul>
            {/* 305: what others said, with names and reasons — the poster and the Gallery roles only. */}
            {state.decides &&
              mine.map((f) =>
                f.answers && f.answers.length > 0 ? (
                  <ul key={f.valueId} className="crowd__answers" aria-label={`${label(f)}: ${t.answers}`}>
                    {f.answers.map((a, i) => (
                      <li key={i} className="crowd__answer">
                        <span className="crowd__who">{a.name}</span> · {a.yes ? t.saidYes(label(f)) : t.saidNo(label(f))}
                        {a.reason ? <span className="crowd__reason"> — “{a.reason}”</span> : null}
                      </li>
                    ))}
                  </ul>
                ) : null,
              )}
          </div>
        )
      })}
      {asking && (
        <TagReason
          kind={asking.kind}
          label={asking.label}
          confidence={asking.confidence}
          busy={busy !== null}
          onSend={(reason) => void answer(asking.valueId, asking.kind === 'add', reason)}
          onCancel={() => setAsking(null)}
        />
      )}
      <div className="photowhat__actions">
        <button type="button" className="btn btn--primary btn--sm" onClick={onClose}>
          {t.done}
        </button>
      </div>
    </Dialog>
  )
}
